import { useEffect, useMemo, useState } from "react";
import { Minus, Plus, Loader2, ArrowRight, MapPin, ShieldCheck, Phone } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../lib/api";
import { PRODUCT_IMAGES, PRODUCT_SUBTITLE, PRODUCT_VOLUME, PRODUCT_PACK, PICKUP_CODE, PICKUP_COLOR, PICKUP_BADGE_CLASS, GRADE_DOT, formatAUD } from "../lib/products";

const PREFILL_KEY = "tnb_customer_prefill";
const EMPTY_CUSTOMER = { customer_name: "", mobile: "", email: "", business_name: "" };

export default function TnbOrder() {
  const [products, setProducts] = useState([]);
  const [qty, setQty] = useState({});
  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);
  const [remember, setRemember] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiClient
      .get("/products")
      .then((r) => {
        setProducts(r.data.products);
        const init = {};
        r.data.products.forEach((p) => (init[p.id] = 0));
        setQty(init);
      })
      .catch(() => toast.error("Failed to load products"));

    // Load saved customer details from previous order on this device.
    // Non-sensitive (name/mobile/email/business_name only; never payment info).
    try {
      const raw = localStorage.getItem(PREFILL_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && typeof saved === "object") {
          setCustomer({
            customer_name: saved.customer_name || "",
            mobile: saved.mobile || "",
            email: saved.email || "",
            business_name: saved.business_name || "",
          });
          setRemember(true);
        }
      }
    } catch (err) {
      // localStorage disabled (e.g. Safari private mode) or malformed value.
      // Feature degrades gracefully: the form starts empty. No user-facing error.
      try { localStorage.removeItem(PREFILL_KEY); } catch { /* ignore */ }
    }
  }, []);

  const totals = useMemo(() => {
    const total = products.reduce(
      (acc, p) => acc + (qty[p.id] || 0) * p.price_aud_inc_gst,
      0
    );
    const gst = total / 11;
    const net = total - gst;
    return { total, gst, net };
  }, [qty, products]);

  const totalQty = Object.values(qty).reduce((a, b) => a + b, 0);

  const setQ = (id, next) =>
    setQty((s) => ({ ...s, [id]: Math.max(0, Math.min(999, next)) }));

  const validate = () => {
    if (totalQty <= 0) return "Add at least one product before checkout.";
    if (!customer.customer_name.trim()) return "Please enter your full name.";
    if (customer.mobile.replace(/\D/g, "").length < 6) return "Please enter a valid mobile number.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(customer.email)) return "Please enter a valid email.";
    if (!customer.business_name.trim()) return "Please enter your business or workshop name.";
    return null;
  };

  const handleCheckout = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      // Save / clear pre-fill data BEFORE redirecting to Stripe.
      // Only stored locally — never sent anywhere until customer submits an order.
      try {
        if (remember) {
          localStorage.setItem(PREFILL_KEY, JSON.stringify(customer));
        } else {
          localStorage.removeItem(PREFILL_KEY);
        }
      } catch (storageErr) {
        // localStorage unavailable (private mode / quota) — non-fatal, checkout continues.
      }
      const items = products.map((p) => ({ product_id: p.id, quantity: qty[p.id] || 0 }));
      const resp = await apiClient.post("/checkout/session", {
        customer,
        items,
        origin_url: window.location.origin,
      });
      window.location.href = resp.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment initialization failed.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-app text-app pb-40">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-app/85 backdrop-blur-md border-b border-app">
        <div className="max-w-md mx-auto px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[19px] font-extrabold tracking-tight leading-none">
              <span className="text-accent-app">Quick Oil Order</span>
            </div>
            <div className="text-[12px] text-muted-app mt-1">
              Pay securely before pickup
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 pt-6">
        {/* Hero */}
        <section className="mb-6">
          <h1 className="text-[28px] font-extrabold leading-[1.1] tracking-tight">
            Choose your <span className="text-accent-app">oil.</span>
            <br />Get trade value. Pick up fast.
          </h1>
          <p className="mt-2.5 text-muted-app text-[14px] leading-relaxed">
            Bulk-buy pricing. Secure checkout. Ready for pickup.
          </p>

          <a
            href="tel:+61426266655"
            data-testid="hero-contact-link"
            className="mt-4 inline-flex items-center gap-2 text-[13px] text-muted-app hover:text-app transition-colors group"
          >
            <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-elev border border-app group-hover:border-accent-app transition-colors">
              <Phone className="h-3.5 w-3.5 text-accent-app" strokeWidth={2.5} />
            </span>
            <span>
              Any other inquiry? Contact us:{" "}
              <span className="font-mono tab-num font-bold text-app group-hover:text-accent-app transition-colors">
                0426 266 655
              </span>
            </span>
          </a>
        </section>

        {/* Products */}
        <SectionTitle>Trade Oil Products</SectionTitle>
        <div className="mt-3 space-y-3">
          {products.map((p) => {
            const q = qty[p.id] || 0;
            const selected = q > 0;
            return (
              <article
                key={p.id}
                data-testid={`product-card-${p.id}`}
                className={`relative bg-elev rounded-2xl border ${
                  selected ? "border-accent-app card-glow-accent" : "border-app"
                } overflow-hidden transition-shadow`}
              >
                <div className="p-4 flex gap-4">
                  <div
                    data-testid={`product-image-${p.id}`}
                    className="flex-shrink-0 h-20 w-20 rounded-xl bg-black overflow-hidden ring-1 ring-app flex items-center justify-center"
                  >
                    <img
                      src={PRODUCT_IMAGES[p.id]}
                      alt={p.name}
                      className="max-w-full max-h-full object-contain select-none pointer-events-none"
                      draggable={false}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-[17px] font-bold tracking-tight text-app leading-none">
                        {p.grade}
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest border rounded-full px-2 py-0.5 ${PICKUP_BADGE_CLASS[p.id]}`}
                        data-testid={`pickup-badge-${p.id}`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${GRADE_DOT[p.id]}`} />
                        {PICKUP_CODE[p.id]} / {PICKUP_COLOR[p.id]}
                      </span>
                    </div>
                    <div className="text-[13px] text-muted-app mt-0.5">
                      {PRODUCT_SUBTITLE[p.id]}
                    </div>
                    <div className="text-[12px] text-subtle-app mt-0.5">
                      {PRODUCT_VOLUME[p.id]}
                      {PRODUCT_PACK[p.id] !== PRODUCT_VOLUME[p.id] && (
                        <> · {PRODUCT_PACK[p.id]}</>
                      )}
                    </div>
                    <div className="mt-2 flex items-baseline gap-1.5">
                      <span className="font-mono tab-num text-[22px] font-bold text-app leading-none">
                        {formatAUD(p.price_aud_inc_gst).replace(".00", "")}
                      </span>
                      <span className="text-[11px] uppercase tracking-wider text-subtle-app">
                        inc GST
                      </span>
                    </div>
                  </div>
                </div>

                <div className="px-4 pb-4 flex items-center justify-between">
                  <div className="inline-flex items-center rounded-full bg-elev-2 border border-app p-1">
                    <button
                      data-testid={`qty-minus-${p.id}`}
                      onClick={() => setQ(p.id, q - 1)}
                      disabled={q === 0}
                      className="pressable h-10 w-10 rounded-full flex items-center justify-center text-app disabled:opacity-30 hover:bg-white/5"
                      aria-label={`Decrease ${p.grade}`}
                    >
                      <Minus className="h-4 w-4" strokeWidth={2.5} />
                    </button>
                    <div
                      data-testid={`qty-value-${p.id}`}
                      className="w-10 text-center font-mono tab-num text-[17px] font-semibold"
                    >
                      {q}
                    </div>
                    <button
                      data-testid={`qty-plus-${p.id}`}
                      onClick={() => setQ(p.id, q + 1)}
                      className="pressable h-10 w-10 rounded-full flex items-center justify-center bg-accent-app text-accent-fg bg-accent-hover-app"
                      aria-label={`Increase ${p.grade}`}
                    >
                      <Plus className="h-4 w-4" strokeWidth={3} />
                    </button>
                  </div>

                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-subtle-app">Line</div>
                    <div
                      data-testid={`line-total-${p.id}`}
                      className="font-mono tab-num text-[16px] font-bold text-app"
                    >
                      {formatAUD(q * p.price_aud_inc_gst)}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Customer details */}
        <div className="mt-8">
          <SectionTitle>Customer Details</SectionTitle>
          <div className="mt-3 bg-elev border border-app rounded-2xl p-4 space-y-3">
            <FormField
              id="customer_name"
              label="Customer Name"
              value={customer.customer_name}
              onChange={(v) => setCustomer({ ...customer, customer_name: v })}
              placeholder="e.g. Alex Smith"
            />
            <FormField
              id="mobile"
              label="Mobile Number"
              type="tel"
              value={customer.mobile}
              onChange={(v) => setCustomer({ ...customer, mobile: v })}
              placeholder="04XX XXX XXX"
            />
            <FormField
              id="email"
              label="Email Address"
              type="email"
              value={customer.email}
              onChange={(v) => setCustomer({ ...customer, email: v })}
              placeholder="you@workshop.com.au"
            />
            <FormField
              id="business_name"
              label="Business / Workshop"
              value={customer.business_name}
              onChange={(v) => setCustomer({ ...customer, business_name: v })}
              placeholder="e.g. Alex Auto Repairs"
            />

            <label className="flex items-center gap-3 mt-2 select-none cursor-pointer">
              <input
                data-testid="remember-details-checkbox"
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 accent-[rgb(var(--accent))] cursor-pointer"
              />
              <span className="text-[12px] text-muted-app leading-snug">
                Save my details on this device for faster next order
              </span>
            </label>
          </div>
        </div>

        {/* Info strip */}
        <div className="mt-6 flex items-center gap-2 text-[12px] text-muted-app">
          <MapPin className="h-3.5 w-3.5 text-accent-app" />
          Payment required before collection · Pickup at counter
        </div>
      </main>

      {/* Sticky checkout summary */}
      <div className="fixed bottom-0 inset-x-0 z-40 border-t border-app bg-app/95 backdrop-blur-md">
        <div className="max-w-md mx-auto px-5 pt-3 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-subtle-app font-semibold">
                Total · GST inc
              </div>
              <div
                data-testid="sticky-total"
                className="font-mono tab-num text-[26px] font-extrabold tracking-tight leading-none mt-0.5"
              >
                {formatAUD(totals.total)}
              </div>
              <div className="text-[11px] text-muted-app mt-1">
                <span data-testid="summary-total-items">{totalQty}</span> item{totalQty === 1 ? "" : "s"}
              </div>
            </div>
            <button
              data-testid="pay-now-button"
              onClick={handleCheckout}
              disabled={submitting || totalQty <= 0}
              className="pressable inline-flex items-center justify-center gap-2 bg-accent-app text-accent-fg bg-accent-hover-app font-extrabold uppercase tracking-wider rounded-full px-7 h-14 disabled:opacity-40 disabled:cursor-not-allowed text-[15px] shadow-lg shadow-black/40"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" /> Loading
                </>
              ) : (
                <>
                  Pay Now
                  <ArrowRight className="h-4 w-4" strokeWidth={3} />
                </>
              )}
            </button>
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-subtle-app">
            <ShieldCheck className="h-3 w-3" />
            Secure checkout powered by Stripe
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 bg-app/50" style={{ background: "rgb(var(--border))" }} />
      <h2 className="text-[11px] uppercase tracking-[0.2em] text-subtle-app font-bold">
        {children}
      </h2>
      <div className="h-px flex-1" style={{ background: "rgb(var(--border))" }} />
    </div>
  );
}

function FormField({ id, label, value, onChange, type = "text", placeholder }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-subtle-app font-semibold mb-1.5">
        {label}
      </span>
      <input
        data-testid={`input-${id}`}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-12 px-4 bg-elev-2 border border-app text-app placeholder:text-subtle-app rounded-xl transition-colors"
        autoComplete="off"
      />
    </label>
  );
}
